// ✅ src/components/screens/StoreSettingsScreen.js (FULL - UPDATED)
// ✅ Upload Logo + Banner via file picker -> Firebase Storage
// ✅ Saves to stores/{uid}
// ✅ Public link uses /s/:storeSlug
// ✅ "Delete store" (HARD DELETE)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth, db, storage, functions } from "../../lib/firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { httpsCallable } from "firebase/functions";

function safe(v) {
  return String(v ?? "").trim();
}
function makeSlug(name) {
  return safe(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}
function initials(name) {
  const t = safe(name);
  if (!t) return "S";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] || "S";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase();
}

export default function StoreSettingsScreen({ goBack }) {
  const [user, setUser] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const [edit, setEdit] = useState({
    storeName: "",
    storeSlug: "",
    locationText: "",
    contactPhone: "",
    contactWhatsApp: "",
    contactEmail: "",
    bio: "",
    logoUrl: "",
    bannerUrl: "",
  });

  // ✅ delete modal state
  const [showDelete, setShowDelete] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setMsg("Please login first.");
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return;

    let alive = true;

    const unsub = onSnapshot(
      doc(db, "stores", user.uid),
      (snap) => {
        if (!alive) return;

        if (!snap.exists()) {
          setStore(null);
          setMsg("No store found for this account.");
          setLoading(false);
          return;
        }

        const data = snap.data() || {};
        setStore({ id: user.uid, ...data });

        setEdit({
          storeName: safe(data.storeName) || safe(user.displayName) || "My Store",
          storeSlug: safe(data.storeSlug),
          locationText: safe(data.locationText),
          contactPhone: safe(data.contactPhone),
          contactWhatsApp: safe(data.contactWhatsApp),
          contactEmail: safe(data.contactEmail) || safe(user.email),
          bio: safe(data.bio),

          // ✅ keep urls but user doesn't type them
          logoUrl: safe(data.logoUrl) || safe(user.photoURL),
          bannerUrl: safe(data.bannerUrl),
        });

        setLoading(false);
      },
      (err) => {
        if (!alive) return;
        setMsg(err?.message || "Failed to load store.");
        setLoading(false);
      }
    );

    return () => {
      alive = false;
      try {
        unsub();
      } catch {}
    };
  }, [user]);

  // ✅ correct public route
  const publicStoreUrl = useMemo(() => {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    const slug = safe(edit.storeSlug) || safe(store?.storeSlug);
    if (slug) return `${base}/s/${slug}`;
    return `${base}/s/${safe(user?.uid)}`;
  }, [edit.storeSlug, store, user]);

  const save = async () => {
    if (!user) return;

    const storeName = safe(edit.storeName);
    if (storeName.length < 2) return setMsg("Store name is required.");

    const slug = safe(edit.storeSlug) || makeSlug(storeName);

    try {
      setSaving(true);
      setMsg("");

      await updateDoc(doc(db, "stores", user.uid), {
        storeName,
        storeSlug: slug,
        locationText: safe(edit.locationText),
        contactPhone: safe(edit.contactPhone),
        contactWhatsApp: safe(edit.contactWhatsApp),
        contactEmail: safe(edit.contactEmail),
        bio: safe(edit.bio),

        // ✅ saved from uploads
        logoUrl: safe(edit.logoUrl),
        bannerUrl: safe(edit.bannerUrl),

        updatedAt: serverTimestamp(),
      });

      setMsg("✅ Saved.");
      setTimeout(() => setMsg(""), 1200);
    } catch (e) {
      setMsg(e?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  async function uploadImageTo(path, file, onProgress) {
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);

    return await new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          onProgress && onProgress(pct);
        },
        (err) => reject(err),
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve(url);
        }
      );
    });
  }

  const onPickLogo = async (e) => {
    if (!user) return;

    const file = e.target.files?.[0];
    e.target.value = ""; // ✅ allow picking same file again
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMsg("Please select an image file.");
      return;
    }

    try {
      setMsg("");
      setUploadingLogo(true);

      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const url = await uploadImageTo(`stores/${user.uid}/logo.${ext}`, file);

      setEdit((s) => ({ ...s, logoUrl: url }));

      // ✅ instant save just for logo
      await updateDoc(doc(db, "stores", user.uid), {
        logoUrl: url,
        updatedAt: serverTimestamp(),
      });

      setMsg("✅ Logo updated.");
      setTimeout(() => setMsg(""), 1200);
    } catch (err) {
      setMsg(err?.message || "Logo upload failed.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const onPickBanner = async (e) => {
    if (!user) return;

    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMsg("Please select an image file.");
      return;
    }

    try {
      setMsg("");
      setUploadingBanner(true);

      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const url = await uploadImageTo(`stores/${user.uid}/banner.${ext}`, file);

      setEdit((s) => ({ ...s, bannerUrl: url }));

      // ✅ instant save just for banner
      await updateDoc(doc(db, "stores", user.uid), {
        bannerUrl: url,
        updatedAt: serverTimestamp(),
      });

      setMsg("✅ Banner updated.");
      setTimeout(() => setMsg(""), 1200);
    } catch (err) {
      setMsg(err?.message || "Banner upload failed.");
    } finally {
      setUploadingBanner(false);
    }
  };

  // ✅ HARD DELETE (Cloud Function)
  const deleteStore = async () => {
    if (!user) return;

    if (deleteText.trim().toUpperCase() !== "DELETE") {
      setMsg('Type "DELETE" to confirm.');
      return;
    }

    try {
      setDeleting(true);
      setMsg("");

      const fn = httpsCallable(functions, "deleteEverything");
      await fn({ confirm: "DELETE" });

      // sign out (best effort)
      try {
        await signOut(auth);
      } catch {}

      // clear cache (best effort)
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch {}

      window.location.replace("/login");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Failed to delete everything.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="ss-page">
      <style>{css}</style>

      <div className="ss-wrap">
        {/* TOP BAR */}
        <div className="ss-top">
          <button className="ss-back" onClick={goBack}>
            ← Back
          </button>

          <div className="ss-title">Store Settings</div>

          <button className="ss-primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>

        {msg ? (
          <div className={`ss-msg ${msg.includes("✅") ? "" : "is-err"}`}>{msg}</div>
        ) : null}
        {loading ? <div className="ss-card">Loading…</div> : null}

        {!loading && store ? (
          <>
            {/* PREVIEW CARD */}
            <div className="ss-card ss-card--hero">
              <div className="ss-hero">
                {edit.bannerUrl ? (
                  <img className="ss-banner" src={edit.bannerUrl} alt="banner" />
                ) : null}
                <div className="ss-heroOverlay" />

                <div className="ss-heroTop">
                  <div className="ss-logoBox">
                    <div className="ss-logo">
                      {edit.logoUrl ? (
                        <img src={edit.logoUrl} alt="logo" />
                      ) : (
                        <span className="ss-initials">{initials(edit.storeName)}</span>
                      )}
                    </div>

                    <div className="ss-logoMeta">
                      <div className="ss-storeName">{safe(edit.storeName) || "My Store"}</div>
                      <div className="ss-storeSub">
                        {safe(edit.locationText) || "Add your location"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ss-heroBottom">
                  <div className="ss-linkRow">
                    <div className="ss-linkLabel">Public store link</div>
                    <div className="ss-linkValue">{publicStoreUrl}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* UPLOADS */}
            <div className="ss-card">
              <div className="ss-rowTitle">
                <div className="ss-subtitle">Images</div>

                <button
                  className="ss-danger"
                  onClick={() => {
                    setMsg("");
                    setDeleteText("");
                    setShowDelete(true);
                  }}
                >
                  Delete store
                </button>
              </div>

              <div className="ss-two">
                <div className="ss-box">
                  <div className="ss-boxTitle">Logo</div>
                  <div className="ss-boxSub">Square image looks best.</div>

                  <button
                    className="ss-upload"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    {uploadingLogo ? "Uploading…" : "Upload Logo"}
                  </button>

                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={onPickLogo}
                    style={{ display: "none" }}
                  />
                </div>

                <div className="ss-box">
                  <div className="ss-boxTitle">Banner</div>
                  <div className="ss-boxSub">Wide image looks best.</div>

                  <button
                    className="ss-upload"
                    onClick={() => bannerInputRef.current?.click()}
                    disabled={uploadingBanner}
                  >
                    {uploadingBanner ? "Uploading…" : "Upload Banner"}
                  </button>

                  <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/*"
                    onChange={onPickBanner}
                    style={{ display: "none" }}
                  />
                </div>
              </div>

              <div className="ss-note">
                ✅ No more typing links. Just upload and it updates your store instantly.
              </div>
            </div>

            {/* DETAILS */}
            <div className="ss-card">
              <div className="ss-subtitle">Store Details</div>

              <div className="ss-grid">
                <div>
                  <div className="ss-label">Store Name</div>
                  <input
                    className="ss-input"
                    value={edit.storeName}
                    onChange={(e) => setEdit((s) => ({ ...s, storeName: e.target.value }))}
                    placeholder="e.g. Kee Collections"
                  />
                </div>

                <div>
                  <div className="ss-label">Store Slug (URL)</div>
                  <input
                    className="ss-input"
                    value={edit.storeSlug}
                    onChange={(e) => setEdit((s) => ({ ...s, storeSlug: e.target.value }))}
                    placeholder="e.g. kee-collections"
                  />
                  <div className="ss-help">This becomes: /s/your-slug</div>
                </div>

                <div>
                  <div className="ss-label">Location</div>
                  <input
                    className="ss-input"
                    value={edit.locationText}
                    onChange={(e) => setEdit((s) => ({ ...s, locationText: e.target.value }))}
                    placeholder="e.g. Accra"
                  />
                </div>

                <div>
                  <div className="ss-label">Phone</div>
                  <input
                    className="ss-input"
                    value={edit.contactPhone}
                    onChange={(e) => setEdit((s) => ({ ...s, contactPhone: e.target.value }))}
                    placeholder="e.g. 024..."
                  />
                </div>

                <div>
                  <div className="ss-label">WhatsApp</div>
                  <input
                    className="ss-input"
                    value={edit.contactWhatsApp}
                    onChange={(e) => setEdit((s) => ({ ...s, contactWhatsApp: e.target.value }))}
                    placeholder="e.g. 024..."
                  />
                </div>

                <div>
                  <div className="ss-label">Email</div>
                  <input
                    className="ss-input"
                    value={edit.contactEmail}
                    onChange={(e) => setEdit((s) => ({ ...s, contactEmail: e.target.value }))}
                    placeholder="e.g. shop@email.com"
                  />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <div className="ss-label">Bio</div>
                  <textarea
                    className="ss-textarea"
                    rows={4}
                    value={edit.bio}
                    onChange={(e) => setEdit((s) => ({ ...s, bio: e.target.value }))}
                    placeholder="Tell customers about your store..."
                  />
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* DELETE MODAL */}
      {showDelete ? (
        <div className="ss-modalBackdrop" onClick={() => !deleting && setShowDelete(false)}>
          <div className="ss-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ss-modalTitle">Delete store & account?</div>

            <div className="ss-modalText">
              This will permanently delete your <b>store, products, orders, images</b> and your{" "}
              <b>account</b>. This cannot be undone.
            </div>

            <div className="ss-label" style={{ marginTop: 8 }}>
              Type <b>DELETE</b> to confirm
            </div>

            <input
              className="ss-input"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder="DELETE"
              disabled={deleting}
            />

            <div className="ss-modalActions">
              <button className="ss-soft" onClick={() => setShowDelete(false)} disabled={deleting}>
                Cancel
              </button>

              <button
                className="ss-danger"
                onClick={deleteStore}
                disabled={deleting || deleteText.trim().toUpperCase() !== "DELETE"}
              >
                {deleting ? "Deleting…" : "Delete forever"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const css = `
.ss-page{
  min-height:100vh;
  background:#f3f6ff;
  padding:16px;
  font-family: Inter, system-ui, Segoe UI, Arial;
  color:#0f172a;
}
.ss-wrap{max-width:1050px;margin:0 auto;display:grid;gap:12px;}
.ss-top{display:flex;align-items:center;justify-content:space-between;gap:10px;}
.ss-back{
  border:1px solid #e7ecf6;
  background:#fff;
  border-radius:12px;
  padding:10px 12px;
  font-weight:900;
  cursor:pointer;
}
.ss-title{font-weight:1100;font-size:20px;}
.ss-primary{
  border:0;
  background:#2f61c5;
  color:#fff;
  border-radius:12px;
  padding:10px 12px;
  font-weight:1000;
  cursor:pointer;
  box-shadow:0 10px 22px rgba(47,97,197,0.18);
}
.ss-primary:disabled{opacity:.6;cursor:not-allowed;}
.ss-soft{
  border:1px solid #e7ecf6;
  background:#ffffff;
  border-radius:12px;
  padding:9px 12px;
  font-weight:950;
  cursor:pointer;
  box-shadow:0 10px 22px rgba(15,23,42,0.05);
}
.ss-danger{
  border:0;
  background:#ef4444;
  color:#fff;
  border-radius:12px;
  padding:10px 12px;
  font-weight:1000;
  cursor:pointer;
  box-shadow:0 10px 22px rgba(239,68,68,0.18);
}
.ss-danger:disabled{opacity:.6;cursor:not-allowed;}
.ss-msg{
  border:1px solid #e7ecf6;
  background:#fff;
  border-radius:14px;
  padding:12px;
  font-weight:900;
}
.ss-msg.is-err{
  border-color:rgba(255,70,120,0.35);
  background:rgba(255,70,120,0.06);
  color:#b91c1c;
}
.ss-card{
  background:#fff;
  border:1px solid #e7ecf6;
  border-radius:18px;
  padding:12px;
  box-shadow:0 10px 26px rgba(15,23,42,0.06);
  display:grid;
  gap:10px;
}
.ss-card--hero{padding:0;overflow:hidden;border-radius:22px;}
.ss-subtitle{font-weight:1100;font-size:16px;}
.ss-note{color:#64748b;font-weight:900;font-size:12px;margin-top:2px;}
.ss-rowTitle{display:flex;align-items:center;justify-content:space-between;gap:10px;}

/* HERO PREVIEW */
.ss-hero{position:relative;height:210px;background:linear-gradient(135deg,#cfe3ff,#e6f0ff);}
.ss-banner{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;}
.ss-heroOverlay{position:absolute;inset:0;background:linear-gradient(180deg,rgba(255,255,255,.15),rgba(255,255,255,.72));}
.ss-heroTop{
  position:absolute;left:0;right:0;top:0;
  display:flex;align-items:flex-start;justify-content:space-between;
  padding:12px;
  gap:12px;
}
.ss-logoBox{display:flex;align-items:center;gap:12px;min-width:0;}
.ss-logo{
  width:56px;height:56px;border-radius:16px;
  border:1px solid rgba(255,255,255,.65);
  background:rgba(255,255,255,.45);
  overflow:hidden;
  display:grid;place-items:center;
  box-shadow:0 10px 24px rgba(15,23,42,.12);
}
.ss-logo img{width:100%;height:100%;object-fit:cover;}
.ss-initials{font-weight:1100;color:#1d4ed8;}
.ss-logoMeta{min-width:0;}
.ss-storeName{
  font-weight:1100;
  font-size:16px;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  max-width: min(62vw, 520px);
}
.ss-storeSub{color:#475569;font-weight:900;font-size:12px;margin-top:2px;}

.ss-heroBottom{
  position:absolute;left:12px;right:12px;bottom:12px;
  background:rgba(255,255,255,.55);
  border:1px solid rgba(255,255,255,.65);
  border-radius:16px;
  padding:10px 12px;
  backdrop-filter: blur(8px);
}
.ss-linkRow{display:grid;gap:4px;}
.ss-linkLabel{color:#64748b;font-weight:900;font-size:12px;}
.ss-linkValue{
  font-weight:950;
  font-size:13px;
  color:#0f172a;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

/* Upload cards */
.ss-two{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.ss-box{
  border:1px solid #eef2ff;
  background:#fbfdff;
  border-radius:16px;
  padding:12px;
  display:grid;
  gap:8px;
}
.ss-boxTitle{font-weight:1100;}
.ss-boxSub{color:#64748b;font-weight:900;font-size:12px;}
.ss-upload{
  border:0;
  background:#0f172a;
  color:#fff;
  border-radius:12px;
  padding:10px 12px;
  font-weight:1000;
  cursor:pointer;
}
.ss-upload:disabled{opacity:.6;cursor:not-allowed;}

/* Form */
.ss-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.ss-label{color:#64748b;font-weight:900;font-size:12px;margin-bottom:6px;}
.ss-help{color:#94a3b8;font-weight:900;font-size:12px;margin-top:6px;}
.ss-input{
  width:100%;
  border:1px solid #cbd5e1;
  border-radius:12px;
  padding:10px;
  font-weight:900;
  outline:none;
  background:#fff;
}
.ss-textarea{
  width:100%;
  border:1px solid #cbd5e1;
  border-radius:12px;
  padding:10px;
  font-weight:900;
  outline:none;
  background:#fff;
  resize:vertical;
}

/* Modal */
.ss-modalBackdrop{
  position:fixed;inset:0;
  background:rgba(15,23,42,.45);
  display:grid;place-items:center;
  padding:16px;
  z-index:9999;
}
.ss-modal{
  width:min(520px, 100%);
  background:#fff;
  border:1px solid #e7ecf6;
  border-radius:18px;
  padding:14px;
  box-shadow:0 24px 60px rgba(15,23,42,.22);
  display:grid;
  gap:10px;
}
.ss-modalTitle{font-weight:1100;font-size:16px;}
.ss-modalText{color:#0f172a;font-weight:900;font-size:13px;line-height:1.35;}
.ss-modalActions{display:flex;justify-content:flex-end;gap:10px;margin-top:8px;}

@media(max-width:820px){
  .ss-grid{grid-template-columns:1fr;}
  .ss-two{grid-template-columns:1fr;}
  .ss-hero{height:230px;}
}
`;
